/**
 * Calendar — outer shell + grid scaffolding for month and week views.
 *
 * Owns the paper bg + outer hairline. Two layout patterns are exposed via
 * subcomponents — pick one.
 *
 * ── Month view ────────────────────────────────────────────────────────────
 *
 *   <Calendar>
 *     <Calendar.MonthHeader weekdays={['Po','Út','St','Čt','Pá','So','Ne']} />
 *     <Calendar.MonthGrid>
 *       {days.map(d => (
 *         <CalendarCell key={d.iso} date={d.day} today={d.today} muted={d.muted}>
 *           {d.events.map(e => <EventChip key={e.id} {...e} />)}
 *         </CalendarCell>
 *       ))}
 *     </Calendar.MonthGrid>
 *   </Calendar>
 *
 * ── Week swimlane (technician schedule) ───────────────────────────────────
 *
 *   <Calendar scroll>
 *     <Calendar.SwimGrid resourceCols={['64px', '124px']} slots={20} minWidth={1240}>
 *       <Calendar.SwimCorner>Datum / Technik</Calendar.SwimCorner>
 *       <Calendar.SwimSlotHeader slots={SLOT_LABELS} majorEvery={2} />
 *       {days.map((day, dIdx) => (
 *         <Calendar.SwimDay
 *           key={day.id}
 *           rows={techs.length}
 *           date={...}
 *           dayLabel={day.label}
 *           dayDate={day.date}
 *           today={day.today}
 *           leadingDivider={dIdx > 0}
 *         >
 *           {techs.map(t => (
 *             <Calendar.SwimRow key={t.id} resource={<TechCell t={t} />} slots={20}>
 *               {events.filter(e => e.day === day.id && e.tech === t.id).map(e => (
 *                 <EventChip key={e.id} {...e} totalSlots={20} />
 *               ))}
 *             </Calendar.SwimRow>
 *           ))}
 *         </Calendar.SwimDay>
 *       ))}
 *     </Calendar.SwimGrid>
 *   </Calendar>
 *
 * Source: .agents/design/project/artboard-radical-plan.jsx:54-151 (week)
 *         month view extrapolated within the design language
 */

import { useEffect, useRef } from 'react';
import type { CSSProperties, MouseEvent as ReactMouseEvent, ReactNode } from 'react';

interface CalendarProps {
  children: ReactNode;
  /** Wrap the body in a scrollable container — used for week swimlanes. */
  scroll?: boolean;
  /**
   * Make the body scroll *vertically* and let the shell fill its parent — used
   * by the month grid so a tall month (6 weeks, cells with many chips) scrolls
   * instead of overflowing the viewport and clipping the last week.
   */
  scrollY?: boolean;
  /**
   * When this value changes, the scroll container snaps back to the top. Pass
   * the displayed period (e.g. week/day) so navigating to a new week scrolls
   * back to Monday instead of staying wherever the previous week was scrolled.
   * Only meaningful together with `scroll`.
   */
  scrollResetKey?: string | number;
  className?: string;
}

export function Calendar({
  children,
  scroll = false,
  scrollY = false,
  scrollResetKey,
  className = '',
}: CalendarProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // The week query keeps the previous week's data on screen
    // (placeholderData) while the next loads, so this container never unmounts
    // across navigation — reset its scroll position imperatively instead.
    scrollRef.current?.scrollTo({ top: 0 });
  }, [scrollResetKey]);
  return (
    <div
      className={
        'flex min-h-0 flex-col overflow-hidden border border-line bg-paper ' +
        // scrollY: fill the parent so the inner overflow-y-auto has a bounded
        // height to scroll within (otherwise the grid just overflows + clips).
        (scrollY ? 'flex-1 ' : '') +
        className
      }
    >
      {scroll ? (
        <div ref={scrollRef} className="flex-1 overflow-auto">{children}</div>
      ) : scrollY ? (
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{children}</div>
      ) : (
        children
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
   Month subcomponents
   ──────────────────────────────────────────────────────────────────────── */

interface MonthHeaderProps {
  /** Seven weekday labels (Mon-first by convention; pass any 7 strings). */
  weekdays: ReadonlyArray<ReactNode>;
  className?: string;
}

function MonthHeader({ weekdays, className = '' }: MonthHeaderProps) {
  if (weekdays.length !== 7) {
    throw new Error('Calendar.MonthHeader expects exactly 7 weekday labels');
  }
  return (
    <div
      className={
        // sticky so the weekday row stays put when the grid scrolls vertically
        // (Calendar `scrollY` month view); a no-op when nothing scrolls.
        'sticky top-0 z-10 grid grid-cols-7 border-b border-line bg-paper font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-3 ' +
        className
      }
    >
      {weekdays.map((w, i) => (
        <div
          key={i}
          className="border-r border-line px-3 py-2.5 last:border-r-0"
        >
          {w}
        </div>
      ))}
    </div>
  );
}
Calendar.MonthHeader = MonthHeader;

interface MonthGridProps {
  children: ReactNode;
  className?: string;
}

function MonthGrid({ children, className = '' }: MonthGridProps) {
  return (
    <div
      className={
        'grid auto-rows-[minmax(108px,1fr)] grid-cols-7 [&>*:nth-child(7n)]:border-r-0 ' +
        className
      }
    >
      {children}
    </div>
  );
}
Calendar.MonthGrid = MonthGrid;

/* ────────────────────────────────────────────────────────────────────────
   Week swimlane subcomponents (resource × time)
   ──────────────────────────────────────────────────────────────────────── */

interface SwimGridProps {
  /** Track templates for the leading sticky columns (e.g. ['64px', '124px']). */
  resourceCols: ReadonlyArray<string>;
  /** How many time slots span the right side. */
  slots: number;
  /** Minimum slot column width — drives horizontal scroll. Defaults to 54px. */
  slotMinPx?: number;
  /** Total grid min-width; pin so the grid scrolls instead of squashing. */
  minWidth?: number | string;
  children: ReactNode;
  className?: string;
}

function SwimGrid({
  resourceCols,
  slots,
  slotMinPx = 54,
  minWidth,
  children,
  className = '',
}: SwimGridProps) {
  const cols =
    resourceCols.join(' ') +
    ` repeat(${slots}, minmax(${slotMinPx}px, 1fr))`;
  const style: CSSProperties = {
    gridTemplateColumns: cols,
    minWidth,
  };
  return (
    // No ARIA `grid` role: this is a CSS layout grid, not a keyboard-
    // navigable data grid — none of the children carry row/gridcell roles,
    // so `role="grid"` was an aria-required-children violation (caught by
    // the 14.8 T6 axe sweep). The slot buttons inside carry their own
    // accessible labels.
    <div style={style} className={'grid text-[11.5px] ' + className}>
      {children}
    </div>
  );
}
Calendar.SwimGrid = SwimGrid;

interface SwimCornerProps {
  children?: ReactNode;
  /** Number of leading resource columns the corner spans (default 2). */
  span?: number;
  className?: string;
}

function SwimCorner({ children, span = 2, className = '' }: SwimCornerProps) {
  return (
    <div
      style={{ gridColumn: `1 / ${span + 1}` }}
      className={
        'sticky left-0 top-0 z-[5] flex items-end border-b border-r border-line bg-paper px-4 py-3.5 font-mono text-[9.5px] uppercase tracking-[0.1em] text-ink-3 ' +
        className
      }
    >
      {children}
    </div>
  );
}
Calendar.SwimCorner = SwimCorner;

interface SwimSlotHeaderProps {
  slots: ReadonlyArray<ReactNode>;
  /** Mark every Nth slot as "major" (full-ink, semibold). Defaults to 2. */
  majorEvery?: number;
  className?: string;
}

function SwimSlotHeader({ slots, majorEvery = 2, className = '' }: SwimSlotHeaderProps) {
  return (
    <>
      {slots.map((label, i) => {
        const major = i % majorEvery === 0;
        return (
          <div
            key={i}
            className={
              'sticky top-0 z-[4] border-b border-line bg-paper py-3 pl-1.5 pr-0 font-mono text-[10.5px] tracking-[-0.02em] ' +
              (major ? 'font-semibold text-ink ' : 'text-ink-3 ') +
              className
            }
          >
            {label}
          </div>
        );
      })}
    </>
  );
}
Calendar.SwimSlotHeader = SwimSlotHeader;

interface SwimDayProps {
  children: ReactNode;
  /** Number of resource rows this day owns. */
  rows: number;
  /** Day-of-week label (e.g. "Pondělí"). */
  dayLabel: ReactNode;
  /** Date label (e.g. "27. dub. 26"). */
  dayDate: ReactNode;
  today?: boolean;
  /** Mono accent eyebrow under the date when `today` (e.g. "— Dnes"). */
  todayMarker?: ReactNode;
  /** Black 1px divider above the day block — used to separate days. */
  leadingDivider?: boolean;
  className?: string;
}

function SwimDay({
  children,
  rows,
  dayLabel,
  dayDate,
  today = false,
  todayMarker,
  leadingDivider = false,
  className = '',
}: SwimDayProps) {
  return (
    <>
      {leadingDivider && (
        <div
          aria-hidden
          className="col-span-full h-0 border-b border-ink"
        />
      )}
      <div
        style={{ gridRow: `span ${rows}` }}
        className={
          'sticky left-0 z-[3] flex flex-col gap-1 border-b border-r border-line bg-paper px-3 py-3.5 ' +
          (today ? 'bg-accent-soft ' : '') +
          className
        }
      >
        <span
          className={
            'text-[14px] font-bold leading-none tracking-[-0.01em] ' +
            (today ? 'text-accent-text' : 'text-ink')
          }
        >
          {dayLabel}
        </span>
        <span className="font-mono text-[10px] tracking-[-0.02em] text-ink-3">
          {dayDate}
        </span>
        {today && todayMarker && (
          <span className="mt-1 font-mono text-[9px] font-semibold uppercase tracking-[0.12em] text-accent-text">
            {todayMarker}
          </span>
        )}
      </div>
      {children}
    </>
  );
}
Calendar.SwimDay = SwimDay;

interface SwimRowProps {
  /** The leading resource cell content (e.g. technician name). */
  resource: ReactNode;
  /** How many time slots in this row (drives the inner grid). */
  slots: number;
  /** Highlight the row's body band — e.g. for "today". */
  highlight?: boolean;
  /** Row height in px. Defaults to 44 (matches the design). */
  rowPx?: number;
  /**
   * Children render inside the relative-positioned slot row. Pass
   * `<EventChip start={…} span={…} totalSlots={slots} />` for each event.
   */
  children?: ReactNode;
  /**
   * When set, empty slot cells become buttons that report the clicked
   * 0-based slot index. Event chips render above and intercept their own
   * clicks, so this only fires on free time. Used for click-to-schedule.
   */
  onSlotClick?: (slotIndex: number, event: ReactMouseEvent) => void;
  /** Accessible label prefix for the slot buttons (e.g. "Naplánovat"). */
  slotLabel?: (slotIndex: number) => string;
  /** Return true to make a slot non-clickable (e.g. a slot in the past). */
  slotDisabled?: (slotIndex: number) => boolean;
  /** `data-testid` of a clickable slot (e2e). */
  slotTestId?: (slotIndex: number) => string;
  /** Sticky `left` (px) of the resource cell — the width of the columns before it. Defaults to 64. */
  resourceLeft?: number;
  className?: string;
}

function SwimRow({
  resource,
  slots,
  highlight = false,
  rowPx = 44,
  children,
  onSlotClick,
  slotLabel,
  slotDisabled,
  slotTestId,
  resourceLeft = 64,
  className = '',
}: SwimRowProps) {
  return (
    <>
      <div
        style={{ left: resourceLeft, height: rowPx }}
        className={
          'sticky z-[3] flex flex-col justify-center border-b border-r border-line bg-paper px-3 ' +
          className
        }
      >
        {resource}
      </div>
      <div
        style={{
          gridColumn: '3 / -1',
          gridTemplateColumns: `repeat(${slots}, 1fr)`,
          height: rowPx,
        }}
        className={
          'relative grid border-b border-line ' +
          (highlight ? 'bg-accent-soft ' : '')
        }
      >
        {Array.from({ length: slots }).map((_, i) => {
          const cellClass =
            'border-r border-line last:border-r-0 ' +
            (i % 2 === 1 ? 'border-r-line-2 [border-right-style:dotted]' : '');
          const clickable = onSlotClick && !slotDisabled?.(i);
          return clickable ? (
            <button
              key={i}
              type="button"
              aria-label={slotLabel ? slotLabel(i) : `Naplánovat slot ${i + 1}`}
              data-testid={slotTestId?.(i)}
              onClick={(e) => onSlotClick?.(i, e)}
              className={
                cellClass +
                ' cursor-pointer transition-colors hover:bg-accent-soft focus-visible:bg-accent-soft focus-visible:outline-none'
              }
            />
          ) : (
            <div key={i} className={cellClass} />
          );
        })}
        {children}
      </div>
    </>
  );
}
Calendar.SwimRow = SwimRow;
